// TEMPORARY — manual visual QA for the failedColumns banner added to PanelContainer.
// Delete this file and its route registration (search "TEMPORARY" in routes.tsx) once
// you're done eyeballing the UI. Not linked from any nav — reachable only by URL.
import { PanelContainer } from '@/app/pages/Dashboards/components/PanelContainer'
import type { FailedColumn } from '@/app/pages/Dashboards/panels/common/failedColumns'
import type { ReactNode } from 'react'

const oneFailedColumn: FailedColumn[] = [
	{ name: 'temperature', error: 'Column no longer exists on this stream', index: 0 },
]

const twoFailedColumns: FailedColumn[] = [
	{ name: 'temperature', error: 'Column no longer exists on this stream', index: 0 },
	{ name: 'humidity', error: 'Access denied for this field', index: 2 },
]

const manyFailedColumns: FailedColumn[] = [
	{ name: 'temperature', error: 'Column no longer exists on this stream', index: 0 },
	{ name: 'humidity', error: 'Access denied for this field', index: 2 },
	{
		name: 'battery_voltage_and_a_much_longer_column_name_to_check_wrapping',
		error: 'Timed out fetching column metadata',
		index: 5,
	},
]

function PreviewCard({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className='flex flex-col gap-2'>
			<p className='text-body font-medium text-muted-foreground'>{label}</p>
			<div className='h-72 w-96 border-2 border-dashed border-muted-foreground/30'>{children}</div>
		</div>
	)
}

export function PanelContainerPreview() {
	return (
		<div className='p-6 space-y-6'>
			<div className='space-y-1'>
				<h1 className='text-lg font-bold'>PanelContainer — failedColumns manual QA (TEMPORARY)</h1>
				<p className='text-body text-muted-foreground'>
					Hover each card's info icon (top-right) to open the popover. Use the ⋮ menu → "Panel Info" to open the
					full-page dialog instead — same banner should appear there too.
				</p>
			</div>

			<div className='flex flex-wrap gap-6'>
				<PreviewCard label='Baseline — no failures, no warnings (icon stays neutral, no banner)'>
					<PanelContainer panelId='preview-baseline' title='Baseline Panel' infoContent={<p>Nothing unusual here.</p>}>
						<div className='flex items-center justify-center h-full text-muted-foreground'>content</div>
					</PanelContainer>
				</PreviewCard>

				<PreviewCard label='Empty failedColumns array (regression: should behave like baseline, not warn)'>
					<PanelContainer
						panelId='preview-empty'
						title='Empty Array Panel'
						failedColumns={[]}
						infoContent={<p>Nothing unusual here.</p>}>
						<div className='flex items-center justify-center h-full text-muted-foreground'>content</div>
					</PanelContainer>
				</PreviewCard>

				<PreviewCard label='Exactly one failed column (singular wording)'>
					<PanelContainer panelId='preview-one' title='One Failed Column' failedColumns={oneFailedColumn}>
						<div className='flex items-center justify-center h-full text-muted-foreground'>content</div>
					</PanelContainer>
				</PreviewCard>

				<PreviewCard label='Two failed columns (plural wording)'>
					<PanelContainer panelId='preview-two' title='Two Failed Columns' failedColumns={twoFailedColumns}>
						<div className='flex items-center justify-center h-full text-muted-foreground'>content</div>
					</PanelContainer>
				</PreviewCard>

				<PreviewCard label='Three failed columns, one with a long name (check wrapping/scroll)'>
					<PanelContainer panelId='preview-many' title='Many Failed Columns' failedColumns={manyFailedColumns}>
						<div className='flex items-center justify-center h-full text-muted-foreground'>content</div>
					</PanelContainer>
				</PreviewCard>

				<PreviewCard label='Combined: failedColumns + hasPartialAccess + infoContent (check stacking order)'>
					<PanelContainer
						panelId='preview-combined'
						title='Combined Warnings'
						failedColumns={oneFailedColumn}
						hasPartialAccess
						partialAccessMessage='You have access to only certain fields, and data is shown based on those fields only.'
						infoContent={<p>This is the panel-specific info content, rendered last.</p>}>
						<div className='flex items-center justify-center h-full text-muted-foreground'>content</div>
					</PanelContainer>
				</PreviewCard>

				<PreviewCard label='Regression: hasInfoWarning alone still colors the icon amber, no banner'>
					<PanelContainer
						panelId='preview-info-warning'
						title='Info Warning Only'
						hasInfoWarning
						infoContent={<p>e.g. LocateDevices' invalid-coordinates table would render here.</p>}>
						<div className='flex items-center justify-center h-full text-muted-foreground'>content</div>
					</PanelContainer>
				</PreviewCard>
			</div>
		</div>
	)
}
